import * as React from "react";
import { WeatherService, WindData } from "../services/WeatherService";

const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export interface UseWindDataResult {
  loading: boolean;
  windData: WindData | null;
  error: string | null;
}

/**
 * Hook to fetch wind data for a specific location
 * Auto-refreshes every 5 minutes
 */
export const useWindData = (
  latitude: number | null | undefined,
  longitude: number | null | undefined
): UseWindDataResult => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [windData, setWindData] = React.useState<WindData | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const weatherService = React.useMemo(() => new WeatherService(), []);

  const fetchWindData = React.useCallback(async () => {
    if (latitude === null || latitude === undefined || longitude === null || longitude === undefined) {
      setWindData(null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await weatherService.getWindData(latitude, longitude);

      if (data) {
        setWindData(data);
        setError(null);
      } else {
        setWindData(null);
        setError("Unable to fetch wind data");
      }
    } catch (err) {
      console.error("[useWindData] Error fetching wind data:", err);
      setWindData(null);
      setError("Error fetching wind data");
    } finally {
      setLoading(false);
    }
  }, [latitude, longitude, weatherService]);

  // Initial fetch
  React.useEffect(() => {
    fetchWindData();
  }, [fetchWindData]);

  // Auto-refresh interval
  React.useEffect(() => {
    if (latitude === null || latitude === undefined || longitude === null || longitude === undefined) {
      return;
    }

    const intervalId = setInterval(fetchWindData, REFRESH_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [fetchWindData, latitude, longitude]);

  return {
    loading,
    windData,
    error,
  };
};
